import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { TIFLUX_PULL_FIELD_LABELS, type TicketDetail, type TifluxFieldValue } from '@erp/shared';
import {
  ArrowRightLeft,
  ChevronDown,
  CircleCheck,
  CircleX,
  RotateCcw,
  Sparkles,
  Timer,
  Unlink,
} from 'lucide-react';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, Dialog } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  useDetachTicketFromMirror,
  usePatchTicket,
  useTicketAction,
  type TicketAction,
} from './tickets-mutations';
import { errorMessage, isTicketMirrored, isTicketReadOnly, mirrorDetachNote } from './ticket-format';
import { MirroredBadge } from './mirrored-badge';
import { TicketStatusChip } from './detail-status-chip';
import { CloseTicketDialog } from './close-ticket-dialog';
import { AiSummaryDialog, useAiActive } from './ai-actions';
import { ticketTifluxPendingsQuery } from './tickets-queries';
import { AppointmentPendingCard } from './appointment-tiflux';
import { TicketDispatchBadge } from './ticket-dispatch-badge';
import { useTicketDispatchMenu } from './ticket-dispatch-block';

/** spec 026: o valor de um lado da pendência (responsável vazio = "sem responsável") */
function pendingValue(v: TifluxFieldValue, field: string): string {
  if (v.label) return v.label;
  return field === 'responsible' && v.key === null ? 'sem responsável' : '—';
}

const CONFIRM_COPY: Record<
  TicketAction,
  { title: string; body: (n: number) => string; cta: string; destructive: boolean }
> = {
  close: {
    title: 'Fechar ticket',
    body: (n) =>
      `O ticket #${n} será marcado como fechado. Você poderá reabri-lo depois, se necessário.`,
    cta: 'Fechar ticket',
    destructive: false,
  },
  cancel: {
    title: 'Cancelar ticket',
    body: (n) =>
      `Tem certeza que deseja cancelar o ticket #${n}? Esta ação pode ser revertida com "Reabrir".`,
    cta: 'Cancelar ticket',
    destructive: true,
  },
  reopen: {
    title: 'Reabrir ticket',
    body: (n) => `O ticket #${n} voltará para o status aberto da mesa.`,
    cta: 'Reabrir ticket',
    destructive: false,
  },
};

/**
 * White header card row: "#N - TÍTULO" (título editável inline) + chip de
 * status + dropdown "Ações do ticket" com diálogos de confirmação.
 */
export function TicketDetailHeader({
  ticket,
  onChangeClient,
}: {
  ticket: TicketDetail;
  onChangeClient: () => void;
}) {
  const patch = usePatchTicket(ticket.ticketNumber);
  const action = useTicketAction(ticket.ticketNumber);
  const [confirm, setConfirm] = useState<TicketAction | null>(null);
  // v11: "Resumir com IA" — só com a integração ativa (GET /ai/status).
  const aiActive = useAiActive();
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  // spec 014: "Disparar agora" / "Tirar da fila" — a mesma regra, mutação e
  // confirmação da visão compacta (useTicketDispatchMenu)
  const dispatchMenu = useTicketDispatchMenu(ticket);

  const isClosedOrCanceled =
    ticket.status.kind === 'closed' || ticket.status.kind === 'canceled';
  // spec 011: mesa espelhada — fechar/reabrir/cancelar/trocar cliente seriam
  // recusados (409) e o título também; só o que LÊ continua disponível
  const mirrored = isTicketMirrored(ticket);
  const copy = confirm ? CONFIRM_COPY[confirm] : null;
  // spec 024 (FR-005): o admin tira UM ticket espelhado do espelho
  const isAdmin = useAuthUser()?.role === 'admin';
  const detach = useDetachTicketFromMirror(ticket.ticketNumber);
  const [detachConfirm, setDetachConfirm] = useState(false);
  const detachedNote = mirrorDetachNote(ticket);
  // spec 026 (FR-005): mudou no TiFlux e o ERP tem outro valor — só ticket
  // fora do espelho tem pendência, então só ele pergunta
  const pendingsQ = useQuery({
    ...ticketTifluxPendingsQuery(ticket.ticketNumber),
    enabled: ticket.mirrorDetachedAt !== null && !mirrored,
  });
  const pendings = pendingsQ.data ?? [];
  const [pendingsOpen, setPendingsOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-1">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <InlineTitle
          ticketNumber={ticket.ticketNumber}
          title={ticket.title}
          onSave={(title) => patch.mutate({ title })}
          disabled={patch.isPending || isTicketReadOnly(ticket)}
        />
        <TicketStatusChip kind={ticket.status.kind} name={ticket.status.name} />
        {/* spec 054: o motivo escolhido ao fechar; a observação no título e no histórico */}
        {ticket.status.kind === 'closed' && ticket.closeReason ? (
          <span className="text-xs text-muted-foreground" title={ticket.closeNote ?? undefined}>
            Motivo: <span className="font-medium text-foreground">{ticket.closeReason.name}</span>
          </span>
        ) : null}
        {mirrored ? <MirroredBadge /> : <TicketDispatchBadge ticketNumber={ticket.ticketNumber} />}
        {detachedNote ? (
          <Badge variant="success" title={detachedNote}>
            Operado no ERP
          </Badge>
        ) : null}
        {pendings.length > 0 ? (
          <button
            type="button"
            onClick={() => setPendingsOpen(true)}
            title="Mudou no TiFlux e o ERP tem outro valor — clique para ver"
          >
            <Badge variant="warning">Mudou no TiFlux</Badge>
          </button>
        ) : null}
        {ticket.runningTimerUserIds.length > 0 ? (
          <span className="inline-flex h-[22px] items-center gap-1 rounded-[4px] border border-primary/30 bg-primary/10 px-2 text-xs font-medium text-primary">
            <Timer className="size-3 animate-pulse" />
            Timer em andamento
          </span>
        ) : null}
      </div>

      {mirrored ? (
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <Button variant="outline" size="sm" onClick={() => setDetachConfirm(true)}>
              <Unlink />
              Operar no ERP
            </Button>
          ) : null}
          {aiActive ? (
            <Button variant="outline" size="sm" onClick={() => setAiSummaryOpen(true)}>
              <Sparkles />
              Resumir com IA
            </Button>
          ) : null}
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={action.isPending}>
              Ações do ticket
              <ChevronDown className="size-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {isClosedOrCanceled ? (
              <DropdownMenuItem onSelect={() => setConfirm('reopen')}>
                <RotateCcw />
                Reabrir ticket
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => setConfirm('close')}>
                <CircleCheck />
                Fechar ticket
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onChangeClient}>
              <ArrowRightLeft />
              Trocar cliente
            </DropdownMenuItem>
            {aiActive ? (
              <DropdownMenuItem onSelect={() => setAiSummaryOpen(true)}>
                <Sparkles />
                Resumir com IA
              </DropdownMenuItem>
            ) : null}
            {dispatchMenu.items}
            {!isClosedOrCanceled ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setConfirm('cancel')}>
                  <CircleX />
                  Cancelar ticket
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {detachConfirm ? (
        <Dialog open onClose={() => setDetachConfirm(false)} title="Operar este ticket no ERP">
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              O ticket #{ticket.ticketNumber} sai do espelho do TiFlux e passa a ser editado aqui: etapa,
              responsável, comentários, apontamentos e fechamento.
            </p>
            <p>
              A partir daqui o espelho não o atualiza mais — nada do que mudar no TiFlux chega a este
              ticket, e nada do que mudar aqui vai para lá. A cópia no TiFlux fica com a equipe de lá.
            </p>
            <p className="font-medium text-foreground">Não há volta pela tela.</p>
          </div>
          {detach.isError ? <p className="mt-2 text-sm text-destructive">{errorMessage(detach.error)}</p> : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDetachConfirm(false)}>
              Voltar
            </Button>
            <Button
              size="sm"
              disabled={detach.isPending}
              onClick={() => detach.mutate(undefined, { onSuccess: () => setDetachConfirm(false) })}
            >
              Operar no ERP
            </Button>
          </div>
        </Dialog>
      ) : null}

      {pendingsOpen ? (
        <Dialog open onClose={() => setPendingsOpen(false)} title="Mudou no TiFlux">
          <div className="min-w-0 space-y-3 text-sm contain-[inline-size]">
            <p className="text-muted-foreground">
              Alguém mexeu neste ticket no TiFlux depois que ele passou a ser operado aqui. Nada no ERP
              mudou sozinho.
            </p>
            <ul className="space-y-2">
              {pendings.map((p) =>
                // ADR-013: o apontamento editado no ERP que o TiFlux mudou depois
                p.field === 'appointment' ? (
                  <li key={`appointment-${p.appointmentId}`}>
                    <AppointmentPendingCard pending={p} />
                  </li>
                ) : (
                  <li key={p.field} className="rounded-md border px-3 py-2">
                    <p className="font-medium">{TIFLUX_PULL_FIELD_LABELS[p.field]}</p>
                    <p className="min-w-0 truncate text-xs">
                      TiFlux: <strong>{pendingValue(p.tiflux, p.field)}</strong>
                    </p>
                    <p className="min-w-0 truncate text-xs text-muted-foreground">
                      ERP: {pendingValue(p.erp, p.field)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Visto em {new Date(p.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </li>
                )
              )}
            </ul>
            <p className="text-muted-foreground">
              Um administrador decide o que puxar em{' '}
              <Link
                to="/clients/$clientId"
                params={{ clientId: String(ticket.client.id) }}
                className="text-primary hover:underline"
              >
                {ticket.client.name}
              </Link>{' '}
              → Puxar do TiFlux.
            </p>
          </div>
        </Dialog>
      ) : null}

      {aiSummaryOpen ? (
        <AiSummaryDialog
          ticketNumber={ticket.ticketNumber}
          onClose={() => setAiSummaryOpen(false)}
        />
      ) : null}

      {dispatchMenu.dialog}

      {/* spec 054: fechar pede o motivo da mesa (quando ela tem) e a observação */}
      {confirm === 'close' ? (
        <CloseTicketDialog ticketNumber={ticket.ticketNumber} onClose={() => setConfirm(null)} />
      ) : confirm && copy ? (
        <Dialog open onClose={() => setConfirm(null)} title={copy.title}>
          <p className="text-sm text-muted-foreground">{copy.body(ticket.ticketNumber)}</p>
          {action.isError ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage(action.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>
              Voltar
            </Button>
            <Button
              variant={copy.destructive ? 'destructive' : 'default'}
              size="sm"
              disabled={action.isPending}
              onClick={() => action.mutate(confirm, { onSuccess: () => setConfirm(null) })}
            >
              {copy.cta}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

/** "#N - TÍTULO" 20px/600 — o título entra em edição ao clicar (tickets abertos). */
function InlineTitle({
  ticketNumber,
  title,
  onSave,
  disabled,
}: {
  ticketNumber: number;
  title: string;
  onSave: (title: string) => void;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);

  useEffect(() => {
    setValue(title);
  }, [title]);

  const commit = () => {
    setEditing(false);
    const next = value.trim();
    if (next && next !== title) onSave(next);
    else setValue(title);
  };

  return (
    <h1 className="flex min-w-0 flex-1 items-center gap-1.5 text-xl leading-tight font-semibold text-foreground">
      <span className="shrink-0">#{ticketNumber} -</span>
      {editing ? (
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setValue(title);
              setEditing(false);
            }
          }}
          className="h-8 min-w-52 flex-1 text-base font-semibold"
          autoFocus
        />
      ) : (
        <button
          type="button"
          className={cn(
            '-mx-1 min-w-0 truncate rounded px-1 text-left',
            !disabled && 'cursor-text hover:bg-muted'
          )}
          title={disabled ? undefined : 'Clique para editar o título'}
          onClick={() => {
            if (!disabled) setEditing(true);
          }}
        >
          {title}
        </button>
      )}
    </h1>
  );
}
